import { ChakraProvider } from '@chakra-ui/react'
import { datadogRum } from '@datadog/browser-rum'
import { Global } from '@emotion/react'
import { createRoot } from 'react-dom/client'

import { App } from './App'
import { AdExtensionSniffer } from './components/AdExtensionSniffer/AdExtensionSniffer'
import { ServiceWorker } from './components/ServiceWorker/ServiceWorker'
import { GlobalProvider } from './data/stores/rootStore'
import Theme from './theme/theme'

const sanitizeDatadogTagValue = (value: string) => {
  // Datadog tag values are restricted; avoid characters like '+' from SemVer build metadata.
  // Common allowed set: alphanumerics plus '_', '-', '.', '/', ':'
  return String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_.:\/-]/g, '_')
    .slice(0, 200)
}

if (import.meta.env.VITE_ENV !== 'DEV') {
  datadogRum.init({
    applicationId: '6e0df1ba-f240-45ca-9203-c922be44bee0',
    clientToken: 'pub551178126fd430cb922563ae9b0b8ae4',
    site: 'datadoghq.com',
    service: 'clsp-score',
    env: import.meta.env.VITE_DATADOG_ENV,
    version: sanitizeDatadogTagValue(__APP_VERSION__),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    enableExperimentalFeatures: ['clickmap'],
    // Disable injecting Datadog trace headers (x-datadog-*) into application
    // requests. When `allowedTracingOrigins` is empty, the RUM SDK will not
    // add correlation headers to outgoing XHR/fetch requests, preventing the
    // backend from receiving `x-datadog-origin` and related headers.
    allowedTracingOrigins: [],
    defaultPrivacyLevel: 'allow',
  })

  datadogRum.startSessionReplayRecording()
}

const AppWithServiceWorker = () => {
  return (
    <>
      <App />
      <ServiceWorker />
      <AdExtensionSniffer />
    </>
  )
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(document.getElementById('root')!)

root.render(
  <ChakraProvider theme={Theme}>
    <Global
      styles={{
        html: {
          fontFamily: 'Source Sans Pro, sans-serif',
        },
        body: {
          backgroundColor: Theme.colors.cls.backgroundGray,
        },
      }}
    />
    <GlobalProvider>
      <AppWithServiceWorker />
    </GlobalProvider>
  </ChakraProvider>
)
